// frontend/src/components/write/EditorBlock.jsx
import React from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateLeft, faRotateRight } from "@fortawesome/free-solid-svg-icons";
import "./editorBlock.css";

export default function EditorBlock({
  // Editor refs / config
  quillRef,
  isReadOnlyDraft,
  handlePaste,
  modules,

  // Suggestion mode
  suggestionMode,
  setSuggestionMode,
  suggestionModeRef,
  acceptAllSuggestions,
  rejectAllSuggestions,
  onAcceptPointerDown,
  onRejectPointerDown,

  // Undo/redo
  handleUndo,
  handleRedo,
  canUndo,
  canRedo,

  // Tooltip
  toggleTooltip,
  visibleTooltip,

  // Word count / inline feedback
  wordCount,
  hasInlineFeedback,
  setHasInlineFeedback,
  runInlineFeedback,

  // Inline comment priming
  primeInlineComment,
  currentArticleId,

  // Focus Mode Toggle
  headerRight,

  // Focus Mode
  isFocusMode,

  // Saved indicator
  topbarRight,
}) {
  return (
    <div className={`editor-block-root ${isFocusMode ? "isFocusMode" : ""}`}>
      {!isFocusMode && (
        <>
          <div className="writeFormGroup content-section">
            <label className="content-label">Content</label>
          </div>

          <div className="writeFormGroup content-section">
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, marginBottom: 8 }}>
              <label className="suggestion-toggle">
                <input
                  type="checkbox"
                  checked={suggestionMode}
                  disabled={isReadOnlyDraft}
                  onChange={(e) => {
                    setSuggestionMode(e.target.checked);
                    suggestionModeRef.current = e.target.checked;
                  }}
                />
                <span className="toggle-slider"></span>
                <span className="toggle-label">Suggestion Mode</span>
              </label>

              {suggestionMode && (
                <>
                  <button
                    type="button"
                    className="acceptAllButton"
                    disabled={isReadOnlyDraft}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseUp={(e) => e.preventDefault()}
                    onClick={() => acceptAllSuggestions(quillRef.current?.getEditor?.())}
                  >
                    Accept All
                  </button>

                  <button
                    type="button"
                    className="rejectAllButton"
                    disabled={isReadOnlyDraft}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseUp={(e) => e.preventDefault()}
                    onClick={() => rejectAllSuggestions(quillRef.current?.getEditor?.())}
                  >
                    Reject All
                  </button>

                  <button
                    type="button"
                    className="acceptAllButton"
                    disabled={isReadOnlyDraft}
                    onPointerDown={onAcceptPointerDown}
                    tabIndex={-1}
                  >
                    Accept
                  </button>

                  <button
                    type="button"
                    className="rejectAllButton"
                    disabled={isReadOnlyDraft}
                    onPointerDown={onRejectPointerDown}
                    tabIndex={-1}
                  >
                    Reject
                  </button>
                </>
              )}
            </div>
          </div>

          <div
            className="writeFormGroup content-section suggestion-mode-hint"
          >
            <span className="dc-hint-editor">
              When Suggestion Mode is on, additions appear with a green highlight and underline, and deletions appear with a red highlight and strikethrough. You can accept or reject them later.
            </span>
          </div>
        </>
      )}

      <div className="editor-action-row">
        <div className="editor-action-left">{headerRight}</div>

        <div className="editor-action-buttons">
          <button
            type="button"
            onClick={handleUndo}
            disabled={isReadOnlyDraft || !canUndo}
            title="Undo (Cmd/Ctrl+Z)"
            aria-label="Undo"
            className="acceptAllButton"
            style={{ opacity: canUndo ? 1 : 0.5, cursor: canUndo ? "pointer" : "default" }}
          >
            <FontAwesomeIcon icon={faRotateLeft} /> Undo
          </button>

          <button
            type="button"
            onClick={handleRedo}
            disabled={isReadOnlyDraft || !canRedo}
            title="Redo (Shift+Cmd/Ctrl+Z)"
            aria-label="Redo"
            className="rejectAllButton"
            style={{ opacity: canRedo ? 1 : 0.5, cursor: canRedo ? "pointer" : "default" }}
          >
            <FontAwesomeIcon icon={faRotateRight} /> Redo
          </button>
        </div>
      </div>

      <div className="writeFormGroup content-section">
        <ReactQuill
          ref={quillRef}
          readOnly={isReadOnlyDraft}
          style={{ border: "none" }}
          onPaste={handlePaste}
          modules={modules}
          formats={[
            "header",
            "font",
            "size",
            "bold",
            "italic",
            "underline",
            "strike",
            "blockquote",
            "list",
            "bullet",
            "indent",
            "link",
            "image",
            "video",
            "customImage",
            "passive-voice",
            "long-sentence",
            "suggest-insert",
            "suggest-delete",
            "inlineComment",
          ]}
          className="writeText"
        />
      </div>

      <div className="writeFormGroup word-count-row">
        <label style={{ visibility: "hidden", fontSize: "0px" }}>Label</label>

        <div style={{ fontSize: "12px", fontWeight: "bold", display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
          <span>
            Word Count: {wordCount}
            <button
              type="button"
              className="info-icon"
              onClick={() => toggleTooltip("wordCount")}
              aria-label="Word count help"
              style={{ marginLeft: "8px" }}
            >
              i
            </button>
            {visibleTooltip === "wordCount" && (
              <span className="tooltip">Minimum 500 words</span>
            )}
          </span>
          <span className="saved-indicator">{topbarRight}</span>
        </div>

        {!isFocusMode && (
          <>
            {!hasInlineFeedback ? (
              <button
                className="runInlineFeedbackButton"
                disabled={isReadOnlyDraft}
                onClick={runInlineFeedback}
                type="button"
              >
                Run Inline Feedback
              </button>
            ) : (
              <button
                className="clearInlineFeedbackButton"
                type="button"
                onClick={() => {
                  const quill = quillRef.current?.getEditor();
                  if (!quill) return;
                  const length = quill.getLength();
                  ["passive-voice", "long-sentence"].forEach((blot) => {
                    quill.formatText(0, length, blot, false);
                  });
                  setHasInlineFeedback(false);
                }}
              >
                Clear Inline Feedback
              </button>
            )}

            <button
              type="button"
              className="writeInlineBtn"
              onClick={primeInlineComment}
              disabled={!currentArticleId}
              title="Select text in the editor first"
            >
              Add Inline Comment
            </button>
          </>
        )}
      </div>
    </div>
  );
}